import Airtable from 'airtable';

let airtableBase;

export const AIRTABLE_NOT_CONFIGURED = 'AIRTABLE_NOT_CONFIGURED';
export const READ_ONLY_PREVIEW = 'READ_ONLY_PREVIEW';
export const SESSION_ERROR_CODES = [
  'INVALID_SESSION',
  'SESSION_REQUIRED',
];
export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please log in again.';

const PROXY_TIMEOUT_MS = 45000;

const proxyEndpoint = () => process.env.REACT_APP_AIRTABLE_PROXY_ENDPOINT;

export const isProxyDataSource = () => Boolean(proxyEndpoint());

export const isReadOnlyDataSource = () => (
  isProxyDataSource() && process.env.REACT_APP_CLOUDFLARE_READ_ONLY === 'true'
);

const proxyRequest = async (action, payload = {}) => {
  const endpoint = proxyEndpoint();
  if (!endpoint) {
    const error = new Error('Verification data is not configured for this environment.');
    error.code = AIRTABLE_NOT_CONFIGURED;
    throw error;
  }

  let response;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PROXY_TIMEOUT_MS);
  try {
    response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      redirect: 'manual',
      body: JSON.stringify({ action, ...payload }),
      signal: controller.signal,
    });
  } catch {
    const error = new Error('Unable to access verification data.');
    error.code = 'AIRTABLE_UNAVAILABLE';
    throw error;
  } finally {
    clearTimeout(timeout);
  }

  if (response.type === 'opaqueredirect') {
    const error = new Error('Unable to access verification data.');
    error.code = 'AIRTABLE_UNAVAILABLE';
    throw error;
  }

  const isJson = response.headers?.get?.('Content-Type')?.toLowerCase().includes('application/json');
  let result;
  if (isJson) {
    try {
      result = await response.json();
    } catch {
      const error = new Error('Unable to access verification data.');
      error.code = 'AIRTABLE_UNAVAILABLE';
      throw error;
    }
  } else if (response.status === 401 || response.status === 403) {
    const error = new Error('Session is required.');
    error.code = 'SESSION_REQUIRED';
    throw error;
  } else {
    const error = new Error('Unable to access verification data.');
    error.code = 'AIRTABLE_UNAVAILABLE';
    throw error;
  }

  if (!response.ok) {
    const error = new Error(result?.error || 'Unable to access verification data.');
    error.code = result?.code || 'AIRTABLE_UNAVAILABLE';
    if (result?.writeOutcomeUnknown === true) error.writeOutcomeUnknown = true;
    throw error;
  }
  return result;
};

export const handleSessionError = async (
  error,
  onLogout,
  alertUser = (message) => window.alert(message),
) => {
  if (!SESSION_ERROR_CODES.includes(error?.code)) return false;
  alertUser(SESSION_EXPIRED_MESSAGE);
  if (typeof onLogout === 'function') await onLogout();
  return true;
};

const invalidProxyResponse = () => {
  const error = new Error('Unable to access verification data.');
  error.code = 'AIRTABLE_UNAVAILABLE';
  return error;
};

const resultArray = (result, key) => {
  if (!Array.isArray(result?.[key])) throw invalidProxyResponse();
  return result[key];
};

const resultObject = (result, key) => {
  if (!result?.[key] || typeof result[key] !== 'object' || Array.isArray(result[key])) {
    throw invalidProxyResponse();
  }
  return result[key];
};

const isNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;

const validLanguage = (language) => (
  language
  && typeof language === 'object'
  && !Array.isArray(language)
  && isNonEmptyString(language.id)
  && isNonEmptyString(language.name)
  && isNonEmptyString(language.uuid)
);

const validTranslation = (translation) => (
  translation
  && typeof translation === 'object'
  && !Array.isArray(translation)
  && isNonEmptyString(translation.id)
  && typeof translation.phrase === 'string'
  && Array.isArray(translation.elderVerified)
  && Array.isArray(translation.elderSkipped)
);

const validVerifiedTranslation = (translation) => (
  translation
  && typeof translation === 'object'
  && !Array.isArray(translation)
  && isNonEmptyString(translation.id)
  && typeof translation.phrase === 'string'
);

const getProxyLanguageData = async (languageUuid) => {
  const result = await proxyRequest('languageData', { languageUuid });
  if (typeof result?.languageName !== 'string') throw invalidProxyResponse();
  if (result.verifiedTruncated !== undefined && typeof result.verifiedTruncated !== 'boolean') {
    throw invalidProxyResponse();
  }
  const translations = resultArray(result, 'translations');
  const verifiedTranslations = resultArray(result, 'verifiedTranslations');
  if (!translations.every(validTranslation) || !verifiedTranslations.every(validVerifiedTranslation)) {
    throw invalidProxyResponse();
  }
  return {
    languageName: result.languageName,
    translations,
    verifiedTruncated: result.verifiedTruncated === true,
    verifiedTranslations,
    partial: false,
  };
};

const readOnlyError = () => {
  const error = new Error('Changes are disabled in the read-only Cloudflare preview.');
  error.code = READ_ONLY_PREVIEW;
  return error;
};

const base = (tableName) => {
  if (!airtableBase) {
    const apiKey = process.env.REACT_APP_AIRTABLE_API_KEY;
    if (!apiKey) {
      const error = new Error('Verification data is not configured for this environment.');
      error.code = AIRTABLE_NOT_CONFIGURED;
      throw error;
    }

    airtableBase = new Airtable({ apiKey }).base('appxMcfhx04xO63hf');
  }

  return airtableBase(tableName);
};

export const getTranslations = async (languageUuid) => {
  if (isProxyDataSource()) {
    return (await getProxyLanguageData(languageUuid)).translations;
  }

  const records = await base('Translations').select({
    filterByFormula: `{Language UUID} = '${languageUuid}'`
  }).all(); // Use .all() to fetch all records in one call

  const phraseIds = records
    .map(record => record.get('Phrase'))
    .filter(links => Array.isArray(links) && links.length > 0)
    .map(links => links[0])
    .filter(Boolean);

  if (phraseIds.length === 0) {
    return [];
  }

  const phraseRecords = await base('Phrases').select({
    filterByFormula: `OR(${phraseIds.map(id => `RECORD_ID() = '${id}'`).join(',')})`
  }).all();

  const phrases = phraseRecords.reduce((acc, record) => {
    acc[record.id] = record.get('Word');
    return acc;
  }, {});

  const translations = records
    .map(record => {
      const phraseLink = record.get('Phrase');
      const phraseId = Array.isArray(phraseLink) && phraseLink.length > 0 ? phraseLink[0] : null;

      if (!phraseId || !phrases[phraseId]) {
        return null;
      }

      return {
        id: record.id,
        phrase: phrases[phraseId],
        translation: record.get('Translation'),
        language: record.get('Language'),
        elderVerified: record.get('Elder Verified') || [],
        elderSkipped: record.get('Elder Skipped') || [],
      };
    })
    .filter(Boolean);

  return translations;
};

export const updateTranslation = async (id, updates) => {
  if (isProxyDataSource()) {
    if (isReadOnlyDataSource()) throw readOnlyError();
    await proxyRequest('updateTranslation', { id, updates });
    return;
  }
  await base('Translations').update(id, updates);
};

export const getElder = async (email) => {
  if (isProxyDataSource()) {
    const result = await proxyRequest('login', { email });
    if (
      result?.elder !== null
      && (!result?.elder || typeof result.elder !== 'object' || Array.isArray(result.elder))
    ) {
      throw invalidProxyResponse();
    }
    if (result.elder !== null && (
      !isNonEmptyString(result.elder.id)
      || !result.elder.fields
      || typeof result.elder.fields !== 'object'
      || !Array.isArray(result.elder.fields['Language(s) Spoken'])
    )) {
      throw invalidProxyResponse();
    }
    return result.elder;
  }

  const records = await base('Elders').select({
    filterByFormula: `{Email} = '${email}'`
  }).firstPage();

  return records.length > 0 ? records[0] : null;
};

export const getLanguageNames = async (languageIds) => {
  if (!Array.isArray(languageIds) || languageIds.length === 0) {
    return [];
  }
  if (isProxyDataSource()) {
    const result = await proxyRequest('languages');
    const requestedIds = new Set(languageIds);
    return resultArray(result, 'languages')
      .filter((language) => requestedIds.has(language?.id) && validLanguage(language));
  }

  const records = await base('Indigenous Languages').select({
    filterByFormula: `OR(${languageIds.map(id => `RECORD_ID() = '${id}'`).join(',')})`
  }).firstPage();

  return records
    .map(record => ({
      id: record.id,
      name: record.get('Connected North Name') || record.get('Name'),
      uuid: record.get('uuid')
    }));
};

export const getElderVerificationCounts = async (elderId) => {
  if (isProxyDataSource()) {
    const result = await proxyRequest('counts');
    const counts = resultObject(result, 'counts');
    if (
      !Number.isFinite(counts.totalVerified)
      || !Number.isFinite(counts.totalVerifiedToday)
    ) {
      throw invalidProxyResponse();
    }
    return counts;
  }

  const record = await base('Elders').find(elderId);
  return {
    totalVerified: record.get('Total Verified') || 0,
    totalVerifiedToday: record.get('Total Verified Today') || 0,
  };
};

export const getLanguageNameByUuid = async (uuid) => {
  if (isProxyDataSource()) {
    return (await getProxyLanguageData(uuid)).languageName;
  }

  const records = await base('Indigenous Languages').select({
    filterByFormula: `{uuid} = '${uuid}'`
  }).firstPage();

  if (records.length > 0) {
    return records[0].get('Connected North Name') || records[0].get('Name');
  }

  return '';
};

export const getVerifiedTranslations = async (elderId, language) => {
  if (isProxyDataSource()) {
    return (await getProxyLanguageData(language)).verifiedTranslations;
  }

  // Fetch the elder's name using the elderId
  const elderRecord = await base('Elders').find(elderId);
  const elderName = elderRecord.get('Name');

  const records = await base('Translations').select({
    filterByFormula: `AND({Language UUID} = '${language}', FIND(";${elderName};", CONCATENATE(";", SUBSTITUTE(ARRAYJOIN({Elder Verified}), ", ", ";"), ";")))`
  }).firstPage();

  const translations = await Promise.all(records.map(async (record) => {
    const phraseRecord = await base('Phrases').find(record.get('Phrase')[0]);
    return {
      id: record.id,
      phrase: phraseRecord.get('Word'),
      translation: record.get('Translation'),
      language: record.get('Language'),
    };
  }));
  return translations;
};

export const getLanguageVerificationData = async (elderId, languageUuid) => {
  if (isProxyDataSource()) {
    return getProxyLanguageData(languageUuid);
  }

  const translations = await getTranslations(languageUuid);
  const [verifiedResult, languageNameResult] = await Promise.allSettled([
    getVerifiedTranslations(elderId, languageUuid),
    getLanguageNameByUuid(languageUuid),
  ]);
  if (verifiedResult.status === 'rejected') {
    console.error('Unable to load verified translations:', verifiedResult.reason);
  }
  if (languageNameResult.status === 'rejected') {
    console.error('Unable to load the language name:', languageNameResult.reason);
  }
  const verifiedTranslations = verifiedResult.status === 'fulfilled' ? verifiedResult.value : [];
  const languageName = languageNameResult.status === 'fulfilled' ? languageNameResult.value : '';
  return {
    languageName,
    translations,
    verifiedTruncated: false,
    verifiedTranslations,
    partial: verifiedResult.status === 'rejected' || languageNameResult.status === 'rejected',
  };
};

export const addNewPhrase = async (phrase, translation, elderId, languageId) => {
  if (isProxyDataSource()) {
    if (isReadOnlyDataSource()) throw readOnlyError();
    await proxyRequest('addNewPhrase', { phrase, translation, languageId });
    return;
  }
  const phraseRecord = await base('Phrases').create({
    Word: phrase,
    'Added By': [elderId],
    'Added With': [languageId],
  });
  await base('Translations').create({
    'Phrase': [phraseRecord.id],
    'Translation': translation,
    'Elder Verified': [elderId],
    'Language': [languageId],
  });
};

export const checkPhraseExists = async (phrase) => {
  if (isProxyDataSource()) {
    if (isReadOnlyDataSource()) throw readOnlyError();
    const result = await proxyRequest('checkPhraseExists', { phrase });
    if (typeof result?.exists !== 'boolean') throw invalidProxyResponse();
    return result.exists;
  }
  const records = await base('Phrases').select({
    filterByFormula: `{Word} = '${phrase}'`
  }).firstPage();

  return records.length > 0;
};

export const endReadOnlySession = async () => {
  if (isProxyDataSource()) await proxyRequest('logout');
};
